import React, {FC, useState} from "react"

import Form from "@irati/ui/src/Form"
import {useIntl} from "@irati/utils/lang"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {CompanyNewsTile, CompanyNewsTileConfig, CompanyNewsTileType} from "@irati/types"
import {updateCompanyNewsTile} from "@irati/culture-hub/services/homepage"
import tracking from "@irati-tracking/homepage"
import cultureHubTracking from "@irati-tracking/homepage/cultureHub"
import {triggerErrorToast, triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import {useDeleteCustomerFile} from "@irati/customer-files/hooks/customerFile"

import {useCultureHubStore} from "../../../stores/hooks"
import ContentFooter from "./Footer"
import {SelectedTab} from "./types"
import Content from "./Content"
import PickFromLibrary from "./PickFromLibrary"
import NotificationConfirmation from "./NotificationConfirmation"
import AcceptTerms from "./AcceptTerms"

export const TypeAction = {
  Edit: "edit",
  Create: "create"
} as const

type TypeAction = (typeof TypeAction)[keyof typeof TypeAction]

interface Props {
  companyNewsTile: CompanyNewsTile
  currentTab: SelectedTab
  onClose: () => void
  typeAction: TypeAction
  updateContent: (companyNewsTileConfig: CompanyNewsTileConfig) => void
  companyNewsTileConfig: CompanyNewsTileConfig
}

const CompanyNewsContentForm: FC<Props> = (props) => {
  const {state, dispatch} = useCultureHubStore()
  const {business} = useAuth()
  const {formatMessage} = useIntl()
  const [imageOrTemplateError, setImageOrTemplateError] = useState<string>()
  const [deleteFileMutation] = useDeleteCustomerFile()

  const renderComponent = () => {
    if (state.modalState === "ConfirmationScreen") {
      return (
        <NotificationConfirmation
          onClick={() => {
            cultureHubTracking.clickCultureHubCta("culturehub", "notify_all_confirm")
            updateTile(props.companyNewsTileConfig, props.companyNewsTile, props.onClose, true)
          }}
        />
      )
    }
    if (state.modalState === "AcceptTerms") {
      return <AcceptTerms updateContent={props.updateContent} companyNewsTileConfig={props.companyNewsTileConfig} />
    }
    if (state.modalState === "PickFromLibrary") {
      return <PickFromLibrary companyNewsTileConfig={props.companyNewsTileConfig} updateContent={props.updateContent} />
    }
    return (
      <>
        <Content
          companyNewsTileConfig={props.companyNewsTileConfig}
          updateContent={props.updateContent}
          imageOrTemplateError={imageOrTemplateError}
          removeImageOrTemplateError={() => setImageOrTemplateError(undefined)}
        />
        <ContentFooter
          onSubmit={() => {
            const isValid = validateUploads()
            if (isValid) {
              if (state.isNotificationChecked) {
                dispatch({
                  type: "SET_MODAL_STATE",
                  payload: {
                    modalState: "ConfirmationScreen"
                  }
                })
              } else {
                updateTile(props.companyNewsTileConfig, props.companyNewsTile, props.onClose)
              }
            }
          }}
        />
      </>
    )
  }

  return (
    <Form
      onSubmit={() => {
        const isValid = validateUploads()
        if (isValid) {
          updateTile(props.companyNewsTileConfig, props.companyNewsTile, props.onClose)
        }
      }}>
      {renderComponent()}
    </Form>
  )

  function updateTile(tileConfig: CompanyNewsTileConfig, tile: CompanyNewsTile, onClose: () => void, notify?: boolean) {
    const {typeAction} = props
    const variables = {
      tenantId: business?.uuid,
      tile: {
        uuid: tile.uuid,
        config: tileConfig,
        xpos: tile.xpos,
        ypos: tile.ypos
      },
      notify
    }

    updateCompanyNewsTile(variables)
      .then(() => {
        /** Delete from S3 and DB the previous pdf file if user decides to save the new one */
        if (state.prevFileId && tileConfig.fileId !== state.prevFileId) {
          deleteFileMutation({
            variables: {uuid: state.prevFileId}
          })
        }
        tracking.homePageAddNewTileSubmit(typeAction, tileConfig, state.isNotificationChecked ? "All Employees" : "Not Selected")
        onClose()
        if (notify) {
          triggerSuccessToast(
            formatMessage({id: "culturehub.companyNews.notification.success.notifyMsg"}, {tileTitle: tileConfig?.message}),
            "top-center",
            true
          )
        }
        triggerSuccessToast(
          typeAction == TypeAction.Create
            ? formatMessage({id: "culturehub.companyNews.notification.success.createMsg"}, {tileTitle: tileConfig?.message})
            : formatMessage({id: "culturehub.companyNews.notification.success.updateMsg"}, {tileTitle: tileConfig?.message}),
          "top-center"
        )
        dispatch({type: "RESET"})
      })
      .catch(() => {
        onClose()
        triggerErrorToast(formatMessage({id: "culturehub.companyNews.notification.error.messageOne"}), "top-center")
      })
  }

  function validateUploads() {
    if (props.companyNewsTileConfig.type === CompanyNewsTileType.Video) {
      return true
    }

    if (props.companyNewsTileConfig.type === CompanyNewsTileType.File && !props.companyNewsTileConfig.fileId) {
      dispatch({type: "SET_PDF_ERROR", payload: {isPDFValid: true}})
      return false
    }

    if (!props.companyNewsTileConfig.imageUrl && !props.companyNewsTileConfig.templateIcon) {
      setImageOrTemplateError(formatMessage({id: "culturehub.companyNews.modal.imageOrTemplateValidationMsg"}))
      return false
    }

    return true
  }
}

export default CompanyNewsContentForm
